import type { DiscountRequestDto } from '@gm-commercial/entitlements-client/src/api/entitlementsApi';
import {
  type CaseReducer,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import type { RootState } from '~/app/rootReducer';

import { INITIAL_CATALOG_STATE } from './catalogSlice.constants';
import type {
  CatalogState,
  DeviceOrderConfirmationDetails,
  DeviceOrderPreviewPayload,
  OrderConfirmationDetails,
  OrderPreviewPayload,
} from './catalogSlice.types';

const reduceSetOrderPreview: CaseReducer<
  CatalogState,
  PayloadAction<OrderPreviewPayload>
> = (state, action) => {
  state.orderPreview = {
    ...action.payload,
    selectedVehicles: action.payload.selectedVehicles ?? [],
  };
};

const reduceClearOrderPreview: CaseReducer<
  CatalogState,
  PayloadAction<void>
> = (state) => {
  state.orderPreview = null;
};

const reduceSetOrderConfirmationDetails: CaseReducer<
  CatalogState,
  PayloadAction<OrderConfirmationDetails>
> = (state, action) => {
  state.orderConfirmationDetails = { ...action.payload };
};

const reduceClearOrderConfirmationDetails: CaseReducer<
  CatalogState,
  PayloadAction<void>
> = (state) => {
  state.orderConfirmationDetails = null;
};

const reduceSetDeviceOrderConfirmation: CaseReducer<
  CatalogState,
  PayloadAction<DeviceOrderConfirmationDetails>
> = (state, action) => {
  state.deviceOrderConfirmation = { ...action.payload };
};

const reduceClearDeviceOrderConfirmation: CaseReducer<
  CatalogState,
  PayloadAction<void>
> = (state) => {
  state.deviceOrderConfirmation = null;
};

const reduceSetSelectedVehicles: CaseReducer<
  CatalogState,
  PayloadAction<string[]>
> = (state, action) => {
  if (state.orderPreview) {
    state.orderPreview.selectedVehicles = action.payload;
  }
};

const reduceSetIsEditPaymentModalOpen: CaseReducer<
  CatalogState,
  PayloadAction<boolean>
> = (state, action) => {
  state.isEditPaymentModalOpen = action.payload;
};

const reduceSetPendingDiscount: CaseReducer<
  CatalogState,
  PayloadAction<DiscountRequestDto>
> = (state, action) => {
  state.pendingDiscount = action.payload;
};

const reduceClearPendingDiscount: CaseReducer<
  CatalogState,
  PayloadAction<void>
> = (state) => {
  state.pendingDiscount = null;
};

const reduceSetDeviceOrderPreview: CaseReducer<
  CatalogState,
  PayloadAction<DeviceOrderPreviewPayload>
> = (state, action) => {
  state.deviceOrderPreview = {
    ...action.payload,
    selectedVehicles: action.payload.selectedVehicles ?? [],
  };
};

const reduceClearDeviceOrderPreview: CaseReducer<
  CatalogState,
  PayloadAction<void>
> = (state) => {
  state.deviceOrderPreview = null;
};

export const CATALOG_REDUCERS = {
  reduceSetOrderPreview,
  reduceClearOrderPreview,
  reduceSetOrderConfirmationDetails,
  reduceClearOrderConfirmationDetails,
  reduceSetDeviceOrderConfirmation,
  reduceClearDeviceOrderConfirmation,
  reduceSetSelectedVehicles,
  reduceSetIsEditPaymentModalOpen,
  reduceSetPendingDiscount,
  reduceClearPendingDiscount,
  reduceSetDeviceOrderPreview,
  reduceClearDeviceOrderPreview,
};

export const catalogSlice = createSlice({
  name: 'catalog',
  initialState: INITIAL_CATALOG_STATE,
  reducers: {
    setOrderPreview: reduceSetOrderPreview,
    clearOrderPreview: reduceClearOrderPreview,
    setOrderConfirmationDetails: reduceSetOrderConfirmationDetails,
    clearOrderConfirmationDetails: reduceClearOrderConfirmationDetails,
    setDeviceOrderConfirmation: reduceSetDeviceOrderConfirmation,
    clearDeviceOrderConfirmation: reduceClearDeviceOrderConfirmation,
    setSelectedVehicles: reduceSetSelectedVehicles,
    setIsEditPaymentModalOpen: reduceSetIsEditPaymentModalOpen,
    setPendingDiscount: reduceSetPendingDiscount,
    clearPendingDiscount: reduceClearPendingDiscount,
    setDeviceOrderPreview: reduceSetDeviceOrderPreview,
    clearDeviceOrderPreview: reduceClearDeviceOrderPreview,
  },
});

export const {
  setOrderPreview,
  clearOrderPreview,
  setOrderConfirmationDetails,
  clearOrderConfirmationDetails,
  setDeviceOrderConfirmation,
  clearDeviceOrderConfirmation,
  setSelectedVehicles,
  setIsEditPaymentModalOpen,
  setPendingDiscount,
  clearPendingDiscount,
  setDeviceOrderPreview,
  clearDeviceOrderPreview,
} = catalogSlice.actions;

export const selectCatalogState = (state: RootState) => state.catalog;

export const catalogReducer = catalogSlice.reducer;
